import { useMemo, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Alert,
  Button,
  Form,
  Input,
  message,
  Select,
  Space,
  Spin,
  Typography,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { DisableForViewer } from "../../components/common/DisableForViewer";
import { LinkButton } from "../../components/LinkButton";
import {
  listSitesInfiniteOptions,
  getSiteOptions,
  getOcppConfigurationQueryKey,
} from "../../services/client/@tanstack/react-query.gen";
import { getOcppConfiguration } from "../../services/client/sdk.gen";
import type { SiteReadable as Site } from "../../services/client/types.gen";
import {
  createEmulatedSite,
  type CreateEmulatedSiteRequest,
} from "../../services/emulator/api";
import { FETCH_PAGE_SIZE } from "../../hooks/useCursorPagination";
import { useIsViewer } from "../../hooks/useIsViewer";
import { getOcppBaseUrl } from "../../util/getOcppBaseUrl";

type SocketMapping = {
  socketId: string;
  assetIds: string[];
};

type FormValues = {
  server_url?: string;
  socket_mappings?: SocketMapping[];
};

export const CreateEmulatedSitePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FormValues>();
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const isViewer = useIsViewer();

  // Fetch list of real sites for the dropdown
  const { data: sitesData, isLoading: sitesLoading } = useInfiniteQuery({
    ...listSitesInfiniteOptions({
      query: { page_size: FETCH_PAGE_SIZE },
    }),
    getNextPageParam: (lastPage) => lastPage.end_cursor,
  });

  const allSites = useMemo(
    () => sitesData?.pages.flatMap((p) => p.data) ?? [],
    [sitesData],
  );

  // Fetch selected site details
  const { data: siteDetails, isLoading: siteDetailsLoading } = useQuery({
    ...getSiteOptions({ path: { site_id: selectedSiteId! } }),
    enabled: !!selectedSiteId,
  });

  // Fetch existing OCPP config for selected site
  const {
    data: ocppConfig,
    isLoading: ocppLoading,
    isFetched: ocppFetched,
  } = useQuery({
    queryKey: getOcppConfigurationQueryKey({
      path: { site_id: selectedSiteId! },
    }),
    queryFn: async ({ signal }) => {
      const { data, response } = await getOcppConfiguration({
        path: { site_id: selectedSiteId! },
        signal,
      });
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        throw new Error(
          `Failed to fetch OCPP configuration: ${response.status}`,
        );
      }
      return data;
    },
    enabled: !!selectedSiteId,
  });

  const hasExistingOcpp = ocppFetched && ocppConfig != null;

  // Extract charging pole names from site config
  const chargingPoleNames = useMemo(() => {
    if (!siteDetails) {
      return [];
    }
    return (
      (siteDetails as Site).configurations?.site_configuration?.device_list
        ?.filter(
          (d) =>
            d.type === "charging_pole_3phase" || d.type === "dc_charging_pole",
        )
        .map((d) => d.name) ?? []
    );
  }, [siteDetails]);

  const createMutation = useMutation({
    mutationFn: createEmulatedSite,
    onSuccess: () => {
      message.success("Emulated site created successfully");
      queryClient.invalidateQueries({ queryKey: ["emulatedSites"] });
      navigate("/emulator");
    },
    onError: (error: Error) => {
      message.error(error.message || "Failed to create emulated site");
    },
  });

  const onFinish = (values: FormValues) => {
    if (!selectedSiteId || !siteDetails) {
      return;
    }

    const body: CreateEmulatedSiteRequest = {
      site_id: selectedSiteId,
      partner_id: (siteDetails as Site).partner_id,
    };

    if (hasExistingOcpp && ocppConfig) {
      body.ocpp_config = {
        server_url: `${getOcppBaseUrl()}/ocpp/${selectedSiteId}`,
        socket_to_asset_ids: ocppConfig.socket_to_asset_map ?? {},
      };
    } else if (values.server_url && values.socket_mappings?.length) {
      const socketMap: Record<string, string[]> = {};
      for (const m of values.socket_mappings) {
        if (m.socketId && m.assetIds?.length) {
          socketMap[m.socketId] = m.assetIds;
        }
      }
      body.ocpp_config = {
        server_url: values.server_url,
        socket_to_asset_ids: socketMap,
      };
    }

    createMutation.mutate(body);
  };

  const siteSelected = !!selectedSiteId;
  const detailsReady = siteSelected && !siteDetailsLoading && !ocppLoading;

  return (
    <div className="p-6 max-w-2xl">
      <Typography.Title level={3}>Create Emulated Site</Typography.Title>
      <Typography.Text className="block mb-6 text-gray-500">
        Select a site to create an emulated version for testing.
      </Typography.Text>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        disabled={isViewer}
      >
        {/* Site selection */}
        <Form.Item label="Site" required>
          <Select
            showSearch
            placeholder="Select a site"
            loading={sitesLoading}
            value={selectedSiteId}
            onChange={(value) => {
              setSelectedSiteId(value);
              form.resetFields(["server_url", "socket_mappings"]);
            }}
            optionFilterProp="label"
            options={allSites.map((s) => ({
              label: `${s.name} (${s.site_id})`,
              value: s.site_id,
            }))}
          />
        </Form.Item>

        {/* Partner ID display */}
        {siteDetails && (
          <Form.Item label="Partner ID">
            <Input value={(siteDetails as Site).partner_id} disabled />
          </Form.Item>
        )}

        {/* Loading state while fetching OCPP config */}
        {siteSelected && (siteDetailsLoading || ocppLoading) && (
          <div className="my-4 text-center">
            <Spin size="small" />
            <Typography.Text className="ml-2 text-gray-500">
              Checking site configuration...
            </Typography.Text>
          </div>
        )}

        {/* OCPP config section */}
        {detailsReady && (
          <>
            {hasExistingOcpp ? (
              <Alert
                type="info"
                showIcon
                className="mb-6"
                message="Existing OCPP Configuration"
                description="This site already has an OCPP configuration. It will be used automatically for the emulated site."
              />
            ) : (
              <div className="mb-6 p-4 bg-gray-50 rounded-md border border-solid border-gray-200">
                <Typography.Title level={5} className="!mt-0">
                  OCPP Configuration{" "}
                  <Typography.Text
                    type="secondary"
                    className="text-sm font-normal"
                  >
                    (Optional)
                  </Typography.Text>
                </Typography.Title>
                <Typography.Text type="secondary" className="block mb-4">
                  Only configure this if the emulated site needs OCPP
                  forwarding. You can skip this section entirely.
                </Typography.Text>

                <Form.Item name="server_url" label="OCPP Server URL">
                  <Input placeholder="wss://example.com/ocpp" />
                </Form.Item>

                {chargingPoleNames.length > 0 && (
                  <Form.List name="socket_mappings">
                    {(fields, { add, remove }) => (
                      <>
                        <Typography.Text className="block mb-2">
                          Socket to Asset Mapping
                        </Typography.Text>
                        {fields.map(({ key, name }) => (
                          <Space
                            key={key}
                            className="flex mb-2"
                            align="baseline"
                          >
                            <Form.Item
                              name={[name, "socketId"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Socket ID is required",
                                },
                              ]}
                              className="!mb-0"
                            >
                              <Input
                                placeholder="Socket ID"
                                style={{ width: 200 }}
                              />
                            </Form.Item>
                            <Form.Item
                              name={[name, "assetIds"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Select at least one asset",
                                },
                              ]}
                              className="!mb-0"
                            >
                              <Select
                                mode="multiple"
                                placeholder="Select charging poles"
                                style={{ minWidth: 300 }}
                                options={chargingPoleNames.map((n) => ({
                                  label: n,
                                  value: n,
                                }))}
                              />
                            </Form.Item>
                            <MinusCircleOutlined
                              onClick={() => remove(name)}
                              className="text-gray-400 hover:text-red-500"
                            />
                          </Space>
                        ))}
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          icon={<PlusOutlined />}
                          className="w-full"
                        >
                          Add Socket Mapping
                        </Button>
                      </>
                    )}
                  </Form.List>
                )}
              </div>
            )}
          </>
        )}

        {/* Submit */}
        <Form.Item>
          <Space>
            <DisableForViewer>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isPending}
                disabled={!detailsReady}
              >
                Create Emulated Site
              </Button>
            </DisableForViewer>
            <LinkButton to="/emulator">Cancel</LinkButton>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default CreateEmulatedSitePage;
